import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, RefreshCw, Save, Power, PowerOff, Tag } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

const Coupons = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderAmount: '',
    expiresAt: '',
    usageLimit: '',
    isActive: true,
  });

  const { data: coupons = [], isLoading, refetch } = useQuery({
    queryKey: ['coupons'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/coupons`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_BASE}/api/coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed');
      }
      return res.json();
    },
    onSuccess: () => { toast.success('Coupon created'); refetch(); resetForm(); },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_BASE}/api/coupons/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed');
      }
      return res.json();
    },
    onSuccess: () => { toast.success('Coupon updated'); refetch(); resetForm(); },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_BASE}/api/coupons/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed');
      }
      return res.json();
    },
    onSuccess: () => { toast.success('Coupon deleted'); refetch(); },
    onError: (err) => toast.error(err.message),
  });

  const resetForm = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
    setFormData({ code: '', discountType: 'percentage', discountValue: '', minOrderAmount: '', expiresAt: '', usageLimit: '', isActive: true });
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormData({ code: '', discountType: 'percentage', discountValue: '', minOrderAmount: '', expiresAt: '', usageLimit: '', isActive: true });
    setIsDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      discountType: item.discountType || 'percentage',
      discountValue: item.discountValue?.toString() || '',
      minOrderAmount: item.minOrderAmount?.toString() || '',
      expiresAt: item.expiresAt || '',
      usageLimit: item.usageLimit?.toString() || '',
      isActive: item.isActive !== undefined ? item.isActive : true,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.code || !formData.discountValue) {
      toast.error('Code and discount value are required');
      return;
    }
    if (isNaN(formData.discountValue) || Number(formData.discountValue) <= 0) {
      toast.error('Discount value must be a positive number');
      return;
    }
    const data = {
      code: formData.code,
      discountType: formData.discountType,
      discountValue: parseFloat(formData.discountValue),
      minOrderAmount: parseFloat(formData.minOrderAmount) || 0,
      expiresAt: formData.expiresAt || null,
      usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
      isActive: formData.isActive,
    };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleToggleActive = (item) => {
    updateMutation.mutate({
      id: item.id,
      data: { ...item, isActive: !item.isActive }
    });
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 p-8 shadow-xl">
        <h1 className="text-3xl font-bold text-white flex items-center gap-2">
          <Tag className="w-8 h-8" /> Coupon Management
        </h1>
        <p className="text-white/80">Create and manage discount coupons for your store</p>
      </div>

      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white gap-2">
          <Plus className="w-4 h-4" /> Add Coupon
        </Button>
      </div>

      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Min Order</TableHead>
                <TableHead>Used / Limit</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coupons.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No coupons yet.</TableCell></TableRow>
              ) : (
                coupons.map((item) => (
                  <TableRow key={item.id} className="hover:bg-purple-50/50">
                    <TableCell className="font-mono font-bold text-purple-600">{item.code}</TableCell>
                    <TableCell>
                      {item.discountType === 'percentage'
                        ? `${item.discountValue}%`
                        : `₹${item.discountValue}`}
                    </TableCell>
                    <TableCell>{item.minOrderAmount > 0 ? `₹${item.minOrderAmount}` : '—'}</TableCell>
                    <TableCell>{item.usedCount || 0} {item.usageLimit !== null ? `/ ${item.usageLimit}` : ''}</TableCell>
                    <TableCell>{item.expiresAt ? new Date(item.expiresAt).toLocaleDateString() : 'Never'}</TableCell>
                    <TableCell>
                      {item.isActive ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(item)} title="Toggle Active">
                          {item.isActive ? <Power className="w-4 h-4 text-amber-500" /> : <PowerOff className="w-4 h-4 text-gray-400" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} title="Edit">
                          <Edit className="w-4 h-4 text-indigo-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { if (window.confirm('Delete this coupon?')) deleteMutation.mutate(item.id); }} title="Delete">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Coupon' : 'Add Coupon'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Coupon Code *</Label>
                <Input
                  placeholder="e.g., SAVE10"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label>Discount Type</Label>
                <Select value={formData.discountType} onValueChange={(val) => setFormData({ ...formData, discountType: val })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Value *</Label>
                <Input
                  type="number"
                  placeholder={formData.discountType === 'percentage' ? 'e.g., 10' : 'e.g., 500'}
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                />
              </div>
              <div>
                <Label>Min Order Amount</Label>
                <Input
                  type="number"
                  placeholder="e.g., 1000"
                  value={formData.minOrderAmount}
                  onChange={(e) => setFormData({ ...formData, minOrderAmount: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                />
              </div>
              <div>
                <Label>Usage Limit</Label>
                <Input
                  type="number"
                  placeholder="e.g., 100"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Active</Label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="text-sm text-slate-600">Enabled</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              <Save className="w-4 h-4 mr-2" />
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Coupons;
